import { TrashIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import KeyMessagesList from "@/components/discovery/key-message-selector/KeyMessagesList";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import InlineSearchEditor from "@/components/ui/tiptap-editor/InlineSearchEditor";
import { ExpandedSearchEditor } from "@/components/ui/tiptap-editor/TiptapEditor";
import type { PodcastKMItem } from "@/data-access/discovery-view/queries/key-message-queries";
import { useFeature } from "@/lib/feature-management";
import { addCommasToNumbers } from "@/lib/utils/prettyName";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import { fetchPodcastKeyMessageGroups } from "@/store/discovery/discovery-key-messages/discovery-key-message.async-actions";
import useDiscoveryKeyMessageStore from "@/store/discovery/discovery-key-messages/discovery-key-message.slice";
import { useDiscoveryContext } from "@/store/discovery/discovery-params.context";

const PodcastEpisodesTabContentManagementPanel = () => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const { feedId } = useDiscoveryContext();

  // Feature flag lives here so KeyMessagesList stays decoupled from it
  const isNavV2Stable = useFeature("navigation-v2-stable", false);

  const podcastGroups = useDiscoveryKeyMessageStore(
    (state) => state.podcastGroups,
  );
  const isFetchingPodcast = useDiscoveryKeyMessageStore(
    (state) => state.isFetchingPodcast,
  );
  const podcastGroupsError = useDiscoveryKeyMessageStore(
    (state) => state.podcastGroupsError,
  );
  const podcastKeyMessageIds = useDiscoveryKeyMessageStore(
    (state) => state.selectedPodcastKeyMessageIds,
  );
  const setSelectedPodcastKeyMessageIds = useDiscoveryKeyMessageStore(
    (state) => state.setSelectedPodcastKeyMessageIds,
  );

  const totalCount = useDiscoveryContentManagementStore(
    (state) => state.totalPodcastEpisodesCount,
  );
  const isFetchingEpisodes = useDiscoveryContentManagementStore(
    (state) => state.isFetchingPodcastEpisodes,
  );
  const searchTerm = useDiscoveryContentManagementStore(
    (state) => state.podcastEpisodesSearchTerm,
  );
  const setPodcastEpisodesSearchTerm = useDiscoveryContentManagementStore(
    (state) => state.setPodcastEpisodesSearchTerm,
  );
  const resetPodcastEpisodesTableState = useDiscoveryContentManagementStore(
    (state) => state.resetPodcastEpisodesTableState,
  );
  const selectedPodcastEpisodeIds = useDiscoveryContentManagementStore(
    (state) => state.selectedPodcastEpisodeIds,
  );
  const setSelectedPodcastEpisodeIds = useDiscoveryContentManagementStore(
    (state) => state.setSelectedPodcastEpisodeIds,
  );
  const setPendingPodcastExclusionIds = useDiscoveryContentManagementStore(
    (state) => state.setPendingPodcastExclusionIds,
  );
  const setIsPodcastExcludeModalOpen = useDiscoveryContentManagementStore(
    (state) => state.setIsPodcastExcludeModalOpen,
  );

  useEffect(() => {
    if (feedId) fetchPodcastKeyMessageGroups(String(feedId));
  }, [feedId]);

  const selectedCount = selectedPodcastEpisodeIds.length;

  const handleBulkDelete = useCallback(() => {
    setPendingPodcastExclusionIds(selectedPodcastEpisodeIds);
    setIsPodcastExcludeModalOpen(true);
  }, [
    selectedPodcastEpisodeIds,
    setPendingPodcastExclusionIds,
    setIsPodcastExcludeModalOpen,
  ]);

  const handleSearch = useCallback(
    (value: string) => {
      resetPodcastEpisodesTableState();
      setSelectedPodcastEpisodeIds([]);
      setPodcastEpisodesSearchTerm(value);
    },
    [
      resetPodcastEpisodesTableState,
      setSelectedPodcastEpisodeIds,
      setPodcastEpisodesSearchTerm,
    ],
  );

  const handleRetryPodcastKM = useCallback(() => {
    if (feedId) {
      fetchPodcastKeyMessageGroups(String(feedId));
    }
  }, [feedId]);

  const handleExpand = () => setIsSearchExpanded(true);
  const handleCollapse = () => setIsSearchExpanded(false);

  // Map store groups to the shape expected by KeyMessagesList
  const kmGroups = podcastGroups.map((group) => ({
    id: group.id,
    name: group.name,
    items: group.podcastKeyMessageItems.map((item: PodcastKMItem) => ({
      id: item.id,
      name: item.name,
    })),
  }));

  return (
    <div className="space-y-2 pb-1 pt-6">
      <div className="flex flex-col items-start sm:flex-row sm:justify-between sm:items-center gap-2 px-0 pb-1">
        <KeyMessagesList
          groups={kmGroups}
          isFetching={isFetchingPodcast}
          error={podcastGroupsError}
          onRetry={handleRetryPodcastKM}
          selectedIds={podcastKeyMessageIds}
          onSelectionChange={setSelectedPodcastKeyMessageIds}
          defaultLabel="None selected"
          triggerClassName={isNavV2Stable ? "text-violet-800" : undefined}
        />
        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 h-[30px] px-2.5 text-xs font-normal"
              onPress={handleBulkDelete}
            >
              <TrashIcon size={14} className="text-red-600" />
              Delete ({addCommasToNumbers(selectedCount)})
            </Button>
          )}
          {!isSearchExpanded && (
            <InlineSearchEditor
              handleSearch={handleSearch}
              handleExpand={handleExpand}
              content={searchTerm}
              isSearching={isFetchingEpisodes && !!searchTerm}
            />
          )}
        </div>
      </div>
      {isSearchExpanded && (
        <ExpandedSearchEditor
          handleSearch={handleSearch}
          handleExpand={handleCollapse}
          content={searchTerm}
          isSearching={isFetchingEpisodes && !!searchTerm}
        />
      )}
      <div className="flex items-center justify-between gap-2 px-1.5 pt-2">
        <span className="text-xs text-gray-900">
          {isFetchingEpisodes ? (
            <Skeleton variant="text" className="h-4 w-16" />
          ) : (
            <>
              {addCommasToNumbers(totalCount)}{" "}
              {totalCount === 1 ? "Episode" : "Episodes"}
            </>
          )}
        </span>
      </div>
    </div>
  );
};

export default PodcastEpisodesTabContentManagementPanel;
